import * as _ from 'lodash';

import {
  Category,
  CREATED_AT_SORT,
  EPSS_SORT,
  isLicensingIssueType,
  isQualityIssueType,
  ISSUE_COUNT_SORT,
  isVulnIssueType,
  LICENSING_ISSUE_TYPE_LABELS,
  PACKAGE_SORT,
  QUALITY_ISSUE_TYPE_LABELS,
  SEVERITY_SORT,
  STATUS,
  VULN_ISSUE_TYPE_LABELS,
} from '../../shared/issuesV2';
import { Action, ResourceType } from '../../typings/permissions';
import { window } from '../globals';
import { capitalizeWord } from '../utils';
import type {
  AnySort,
  CreatedAtSort,
  EpssSort,
  Grouping,
  IgnoredKindMetadata,
  IssueCountSort,
  IssueType,
  PackageSort,
  Project,
  SeveritySort,
  Status,
} from '../../shared/issuesV2';
import type { HydrateStateQuery } from '../redux/issuesV2/types';

import { isQualityEnabled, isSecurityEnabled } from './features';
import { parsePaginationQueryParams } from './pagination';

/**
 * Check if a given issueCategory is valid.
 *
 * @param {Category} issueCategory - A given issueCategory to test.
 */
export const isValidCategory = (issueCategory: string): issueCategory is Category =>
  Object.values(Category).includes(issueCategory as Category);

/**
 * Check if a string is a valid grouping
 */
export const isValidGrouping = (grouping: string): grouping is Grouping =>
  ['issue', 'revision'].includes(grouping);

/**
 * Takes the issueCategory and any sort,
 * returns true if a given sort is valid based on that category.
 *
 * @param {Category} issueCategory - An issue category.
 * @param {Grouping} grouping - Current issue grouping.
 * @param {AnySort} sort - Any valid sort string (ie: 'package_asc')
 */
export function isValidSort<S extends AnySort>(issueCategory: Category, grouping: Grouping, sort: S): sort is S {
  // name each check here for better readability below
  const isPackageSort = Object.values(PACKAGE_SORT).includes(sort as PackageSort);
  const isCreatedAtSort = Object.values(CREATED_AT_SORT).includes(sort as CreatedAtSort);
  const isIssueCountSort = Object.values(ISSUE_COUNT_SORT).includes(sort as IssueCountSort);
  const isSeveritySort = Object.values(SEVERITY_SORT).includes(sort as SeveritySort);
  const isEpssSort = Object.values(EPSS_SORT).includes(sort as EpssSort);

  if (grouping === 'issue') {
    switch (issueCategory) {
      case Category.Quality:
      case Category.Licensing:
        return isPackageSort || isCreatedAtSort;

      case Category.Vulnerability:
        return isPackageSort || isCreatedAtSort || isSeveritySort || isEpssSort;

      default:
        return false;
    }
  } else if (grouping === 'revision') {
    return isPackageSort || isCreatedAtSort || isIssueCountSort;
  }

  return false;
}

/**
 * Check if a given status is valid.
 *
 * @param {Status} status - A given status.
 */
export const isValidStatus = (status: Status): status is Status => STATUS.includes(status);

export const getIssueTypeLabel = (type: IssueType): string | null => {
  if (isLicensingIssueType(type)) {
    return LICENSING_ISSUE_TYPE_LABELS[type];
  }
  if (isVulnIssueType(type)) {
    return VULN_ISSUE_TYPE_LABELS[type];
  }
  if (isQualityIssueType(type)) {
    return QUALITY_ISSUE_TYPE_LABELS[type];
  }
  return null;
};

//  Opposite of above. Provide an issue type and get the Category
export const issueTypeToCategory = (issueType: IssueType): Category => {
  if (isLicensingIssueType(issueType)) {
    return Category.Licensing;
  }
  if (isVulnIssueType(issueType)) {
    return Category.Vulnerability;
  }
  if (isQualityIssueType(issueType)) {
    return Category.Quality;
  }
  //  Fallback
  return Category.Licensing;
};

/**
 * Handles converting the query params into their primitive values.
 * This is needed because the parsed params will all be strings,
 * for example `count=123` would parse to `'123'` and needs to be converted
 * to a number before adding it to the state/comparing to current state values.
 */
export const parseQueryParams = (query: Partial<HydrateStateQuery>): Partial<HydrateStateQuery> => {
  const parsed: Partial<HydrateStateQuery> = parsePaginationQueryParams<HydrateStateQuery>(query);

  if (query.sort) {
    parsed.sort = query.sort;
  }

  if (query.grouping) {
    parsed.grouping = query.grouping;
  }

  if (query.search) {
    parsed.search = query.search;
  }

  if (query.status) {
    parsed.status = query.status;
  }

  if (query.issueCategory) {
    parsed.issueCategory = query.issueCategory;
  }

  //  Filters are of type Record<string, string[]>
  if (query.filter) {
    parsed.filter = query.filter;
  }

  if (query.savedFilter) {
    //  The ID of the saved filter
    const numericSavedFilter = Number(query.savedFilter);
    if (Number.isFinite(numericSavedFilter)) {
      parsed.savedFilter = numericSavedFilter;
    }
  }

  return parsed;
};

// extracts the maximum ignoredAt across projects
export const ignoredAtFromProjects = (projects: Project[]): string | undefined =>
  _.max(projects.map((project) => project.ignoredAt));

// extracts the project with the latest ignore at date.
export const getProjectWithTheLatestIgnoredAt = (projects: Project[]): Project | undefined => {
  return _.maxBy(projects, (project) => project.ignoredAt);
};

// extract the ignored kind across projects
export const ignoredKindsFromProjects = (projects: Project[]): IgnoredKindMetadata[] | undefined =>
  _.uniqWith(
    // For now, we don't really care about the issueExceptionId, so we'll omit it to allow for deduping by scope data.
    // We might need it in the future, so for now we'll just cast the type to appease TS.
    projects.map((p) => _.omit(p.ignoredKind, 'issueExceptionId') as IgnoredKindMetadata),
    _.isEqual,
  );

type IssueResourceType =
  | ResourceType.ProjectLicensingIssue
  | ResourceType.ProjectSecurityIssue
  | ResourceType.ProjectQualityIssue;

type IssueResourceTypeAny =
  | ResourceType.ProjectLicensingIssueAny
  | ResourceType.ProjectSecurityIssueAny
  | ResourceType.ProjectQualityIssueAny;

/**
 * Test if a given issue resource type exists in the user permissions.
 *
 * @param {IssueResourceType} issueResourceType - The issue resource type (see permissions ResourceType).
 */
const hasPermissionForIssueResource = (issueResourceType: IssueResourceType) =>
  window.user?.permissions?.some(({ resourceType, action }) =>
    action === Action.View && resourceType === issueResourceType);

/**
 * Test if a given issue resource any type exists in the user permissions.
 *
 * @param {IssueResourceTypeAny} issueResourceType - The issue resource type (see permissions ResourceType).
 */
const hasPermissionForIssueAnyResource = (issueResourceType: IssueResourceTypeAny) => window.hasPermission({
  action: Action.View,
  resourceType: issueResourceType,
});

/**
 * Tests if a user is able to see a specific issue category
 * in the UI for global issues.
 *
 * @param {Category} category - A given issue category.
 */
const userCanViewIssuesGlobally = (category: Category) => {
  switch (category) {
    case Category.Licensing:
      return hasPermissionForIssueResource(ResourceType.ProjectLicensingIssue)
        || hasPermissionForIssueAnyResource(ResourceType.ProjectLicensingIssueAny);
    case Category.Vulnerability:
      return isSecurityEnabled()
        && (hasPermissionForIssueResource(ResourceType.ProjectSecurityIssue)
        || hasPermissionForIssueAnyResource(ResourceType.ProjectSecurityIssueAny));
    case Category.Quality:
      return isQualityEnabled()
        && (hasPermissionForIssueResource(ResourceType.ProjectQualityIssue)
        || hasPermissionForIssueAnyResource(ResourceType.ProjectQualityIssueAny));
    default:
      return false;
  }
};

/**
 * Returns an array of the categories which a user is able to view,
 * based on their permissions and flags enabled.
 */
export const permittedIssueCategoriesToView = (): Category[] => [
  Category.Licensing,
  Category.Vulnerability,
  Category.Quality,
].filter(userCanViewIssuesGlobally);

/**
 * Returns the category label, because we still want to prefix
 * security categories with "Security".
 *
 * @param {Category} category - A given category
 */
export const getCategoryLabel = (category: Category): string => {
  switch (category) {
    case Category.Licensing:
    case Category.Quality:
      return capitalizeWord(category);
    case Category.Vulnerability:
      return 'Security';
    default:
      return '';
  }
};
